import type { LandingPageContent } from '#/domains/landing/types/content';
import {
  hasInlineBenefitCards,
  normalizeLandingPageContent,
} from '#/domains/landing/schema/normalize';
import { getIsAndroid, getIsInApp } from '#/domains/landing/utils/inApp';
import { deriveEventKeys } from '#/domains/landing/utils/slugEventKeys';
import NavBar from './NavBar';
import HeroImage from './HeroImage';
import PageHeader from './PageHeader';
import BodyContent from './BodyContent';
import ContentNav from './ContentNav';
import BenefitCard from './BenefitCard';
import PromoBanner from './PromoBanner';
import StickyCTA from './StickyCTA';
import InlineShare from './InlineShare';

interface LandingPagePreviewProps {
  content: LandingPageContent | null;
  contentBase?: string;
  slugPath?: string;
}

export default function LandingPagePreview({
  content,
  contentBase,
  slugPath,
}: LandingPagePreviewProps) {
  const ctaTrackingKeys = deriveEventKeys(slugPath);
  if (!content) {
    return (
      <div className="flex h-full items-center justify-center text-gray-50">
        <div className="flex flex-col items-center gap-3">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="2" y="3" width="20" height="18" rx="2" />
            <path d="M8 7h8M8 11h5" />
          </svg>
          <p className="text-sm">
            No content yet. Run export from the generator and sync.
          </p>
        </div>
      </div>
    );
  }

  // v1(bodyContent)이든 v2(sections)든 하나의 렌더 모델로 평탄화한 뒤 그린다.
  const model = normalizeLandingPageContent(content);
  const tocPlacement = content.tableOfContents?.placement ?? 'top';
  const hasRootBenefitCards = content.benefitCards.length > 0;
  const hasSectionBenefitCards = hasInlineBenefitCards(model);
  const hasAnyBenefitCards = hasRootBenefitCards || hasSectionBenefitCards;
  // Mobile app users navigate via native chrome — skip the web NavBar entirely.
  // Mobile/desktop web keep the existing fixed nav with its 76px top offset.
  const isInApp = getIsInApp();
  const showNavBar = !isInApp && !content.hideNavBar;

  return (
    <div
      className={`relative flex w-full flex-col bg-white ${
        showNavBar ? 'pt-[76px]' : ''
      }`}
    >
      {showNavBar && (
        <NavBar
          hideBackButton={
            content.hideBackButtonInNav === true ||
            (content.hideBackButtonOnAndroid === true && getIsAndroid())
          }
        />
      )}
      {!content.hideHeroImage && (
        <HeroImage
          src={content.heroImage || undefined}
          contentBase={contentBase}
        />
      )}
      <PageHeader
        tags={content.tagList}
        title={content.pageHeader.title}
        date={content.pageHeader.date}
        profileName={content.pageHeader.profileName}
        profileImage={content.pageHeader.profileImage}
        disclaimer={content.pageHeader.disclaimer}
      />
      {content.tableOfContents && tocPlacement === 'top' && (
        <ContentNav
          tableOfContents={content.tableOfContents}
          targets={model.tableOfContentsTargets}
        />
      )}
      <BodyContent
        sections={model.sections}
        looseParagraphs={model.looseParagraphs}
        contentBase={contentBase}
        afterFirstSection={
          content.tableOfContents && tocPlacement === 'afterFirstSection' ? (
            <ContentNav
              tableOfContents={content.tableOfContents}
              targets={model.tableOfContentsTargets}
              className=""
            />
          ) : undefined
        }
      />
      {hasRootBenefitCards && (
        <div className="flex w-full flex-col gap-5 px-6">
          <h2 className="text-[22px] font-bold leading-[1.5] text-gray-100">
            {content.benefitTitle}
          </h2>
          <div className="flex flex-col gap-4">
            {content.benefitCards.map((card, index) => (
              <BenefitCard
                key={index}
                analyticsId={`benefit-card-root-${card.serviceSeqNo ?? index}`}
                imageUrl={card.imageUrl}
                title={card.title}
                description={card.description}
                supportType={card.supportType}
                organization={card.organization}
                href={
                  card.serviceSeqNo
                    ? `/benefit/${card.serviceSeqNo}`
                    : undefined
                }
                contentBase={contentBase}
              />
            ))}
          </div>
        </div>
      )}
      {!content.hidePromoBanner && (
        <div className={`relative ${hasRootBenefitCards ? '-mt-[132px]' : ''}`}>
          <PromoBanner
            title={content.promoBannerTitle}
            ctaLabel={content.promoBannerCTA}
            image={content.promoBannerImage}
            compact={!hasAnyBenefitCards}
            href={content.ctaHref}
            trackingEventKey={ctaTrackingKeys?.promoCta}
          />
        </div>
      )}
      {content.showInlineShareAtEnd && (
        <InlineShare shareUrl={content.shareUrl} />
      )}
      {!content.hideStickyCTA && (
        <div
          className={
            hasAnyBenefitCards ? 'h-[140px] shrink-0' : 'h-[72px] shrink-0'
          }
        />
      )}
      {!content.hideStickyCTA && (
        <StickyCTA
          label={content.stickyCTA}
          href={content.ctaHref ?? 'https://tl.3o3.co.kr/jcasz4'}
          showShare={content.showShareInCTA === true}
          shareUrl={content.shareUrl}
          trackingEventKey={ctaTrackingKeys?.stickyCta}
        />
      )}
    </div>
  );
}
